import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { InputField, Notice, SelectField } from "./components/workbench";
import { message } from "./cloud";

export type ActionField = {
  name: string;
  label: string;
  type?: "text" | "number";
  optional?: boolean;
  value?: string;
  min?: number;
  max?: number;
  options?: { value: string; label: string }[];
  hint?: string;
};
export function SharedAction({
  label,
  description,
  fields = [],
  submit,
  disabled,
  destructive = false,
  onDone,
}: {
  label: string;
  description: string;
  fields?: ActionField[];
  submit: (data: FormData, key: string) => Promise<unknown>;
  disabled?: boolean;
  destructive?: boolean;
  onDone?: () => void;
}) {
  const [open, setOpen] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false),
    ticket = useRef<{ payload: string; key: string } | null>(null);
  return (
    <Sheet
      open={open}
      onOpenChange={(value) => {
        if (inFlight.current) return;
        setOpen(value);
        if (!value) {
          setError(null);
          ticket.current = null;
        }
      }}
    >
      <SheetTrigger asChild>
        <Button size="sm" variant="outline" disabled={disabled}>
          {label}
        </Button>
      </SheetTrigger>
      <SheetContent className="overflow-y-auto px-6 py-8 sm:max-w-lg">
        <SheetTitle>{label}</SheetTitle>
        <SheetDescription className="mt-2 leading-relaxed">
          {description}
        </SheetDescription>
        <form
          className="mt-6 space-y-5"
          onSubmit={async (event) => {
            event.preventDefault();
            if (inFlight.current) return;
            inFlight.current = true;
            setBusy(true);
            setError(null);
            const data = new FormData(event.currentTarget),
              payload = JSON.stringify([...data.entries()]);
            if (ticket.current?.payload !== payload)
              ticket.current = { payload, key: crypto.randomUUID() };
            try {
              await submit(data, ticket.current.key);
              setOpen(false);
              ticket.current = null;
              onDone?.();
            } catch (cause) {
              setError(message(cause));
            } finally {
              inFlight.current = false;
              setBusy(false);
            }
          }}
        >
          <fieldset className="space-y-5" disabled={busy}>
            {fields.map((field) =>
              field.options ? (
                <SelectField
                  key={field.name}
                  label={field.label}
                  name={field.name}
                  defaultValue={field.value ?? ""}
                  required={!field.optional}
                >
                  <option value="">
                    {field.optional ? "None selected" : "Choose…"}
                  </option>
                  {field.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </SelectField>
              ) : (
                <InputField
                  key={field.name}
                  label={field.label}
                  name={field.name}
                  type={field.type ?? "text"}
                  required={!field.optional}
                  defaultValue={field.value}
                  min={field.min}
                  max={field.max}
                  step={field.type === "number" ? "any" : undefined}
                  maxLength={field.type !== "number" ? 500 : undefined}
                  hint={field.hint}
                />
              ),
            )}
          </fieldset>
          {error && <Notice error>{error}</Notice>}
          <div className="flex flex-wrap gap-3">
            <Button
              type="submit"
              variant={destructive ? "destructive" : "default"}
              disabled={busy}
            >
              {busy ? "Saving…" : label}
            </Button>
            <Button
              variant="ghost"
              type="button"
              disabled={busy}
              onClick={() => {
                setOpen(false);
                ticket.current = null;
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
export const fieldText = (data: FormData, name: string) =>
  String(data.get(name) ?? "").trim();
export const fieldNumber = (data: FormData, name: string) => {
  const value = fieldText(data, name);
  if (!value || !Number.isFinite(Number(value)))
    throw new Error("Enter a valid number.");
  return Number(value);
};
